import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import { isLocalMode } from '@/services/localRuntimeClient';
import {
  getApplication,
  startApplication,
  stopApplication,
  getScaleConfiguration,
  updateScaleConfiguration,
  listRevisions,
  getImageTags,
  setImage,
} from '@/services/applicationManagementApi';
import type { Revision, RunningStatus, ScaleConfiguration, SetImageRequest } from '@/types/automation';

// =============================================================================
// HELPERS
// =============================================================================

/** Token acquisition helper — skips the async fetch in local mode. */
const useTokenFetcher = () => {
  const { getOttoToken } = useAuth();
  return async (): Promise<string> => {
    if (isLocalMode()) return '';
    const token = await getOttoToken();
    if (!token) throw new Error('Failed to acquire Otto token');
    return token;
  };
};

/** Returns true when the status is transitional and should trigger frequent polling. */
const isTransitionalStatus = (status?: RunningStatus | null): boolean =>
  status === 'Starting' || status === 'Stopping';

/** The most recently created revision, or undefined if the list is empty. */
export const getLatestRevision = (revisions: readonly Revision[] | undefined): Revision | undefined =>
  revisions?.reduce<Revision | undefined>((latest, r) =>
    !latest || new Date(r.createdTime).getTime() > new Date(latest.createdTime).getTime() ? r : latest,
  undefined);

/**
 * Returns true while the latest ACA revision is not yet ready to serve traffic.
 * Used to gate Test Draft and to drive revision polling. Failed or torn-down
 * revisions are NOT considered "preparing" — the run itself will surface those.
 *
 * Covers two states:
 * 1. `provisioningState === 'Provisioning'` — revision is being created.
 * 2. `provisioningState === 'Provisioned'` but `replicas.ready === 0` — the
 *    revision exists but the container hasn't started yet (scaling up from zero).
 */
export const isRevisionPreparing = (revisions: readonly Revision[] | undefined): boolean => {
  const latest = getLatestRevision(revisions);
  if (!latest) return false;
  if (latest.provisioningState === 'Provisioning') return true;
  if (latest.provisioningState === 'Provisioned' && latest.replicas.ready === 0) return true;
  return false;
};

// =============================================================================
// QUERY KEYS
// =============================================================================

export const applicationKeys = {
  status: (project: string, app: string) => ['applicationStatus', project.toLowerCase(), app.toLowerCase()] as const,
  scale: (project: string, app: string) => ['scaleConfiguration', project.toLowerCase(), app.toLowerCase()] as const,
  revisions: (project: string, app: string) => ['revisions', project.toLowerCase(), app.toLowerCase()] as const,
  imageTags: (project: string, app: string) => ['imageTags', project.toLowerCase(), app.toLowerCase()] as const,
};

// =============================================================================
// QUERIES
// =============================================================================

/**
 * Fetches the application including its live runningStatus.
 * Polls every 5s when the status is transitional (Starting/Stopping),
 * otherwise uses standard staleTime.
 */
export const useApplicationStatus = (projectName: string, appName: string) => {
  const { isAuthenticated } = useAuth();
  const fetchToken = useTokenFetcher();

  const query = useQuery({
    queryKey: applicationKeys.status(projectName, appName),
    queryFn: async () => {
      const token = await fetchToken();
      return getApplication(projectName, appName, token);
    },
    enabled: (isAuthenticated || isLocalMode()) && !!projectName && !!appName,
    staleTime: 30 * 1000,
    refetchInterval: (query) => {
      const status = query.state.data?.properties?.runningStatus;
      return isTransitionalStatus(status) ? 5_000 : false;
    },
  });

  return query;
};

/**
 * Fetches the scale configuration for an application.
 */
export const useScaleConfiguration = (projectName: string, appName: string) => {
  const { isAuthenticated } = useAuth();
  const fetchToken = useTokenFetcher();

  return useQuery({
    queryKey: applicationKeys.scale(projectName, appName),
    queryFn: async () => {
      const token = await fetchToken();
      return getScaleConfiguration(projectName, appName, token);
    },
    enabled: (isAuthenticated || isLocalMode()) && !!projectName && !!appName,
    staleTime: 60 * 1000,
  });
};

/**
 * Fetches the revision list for an application.
 * Auto-polls every 3s while the latest revision is still provisioning so callers
 * (Test Draft gating, Revisions tab) reflect readiness without a manual refresh.
 */
export const useRevisions = (
  projectName: string,
  appName: string,
  options?: { enabled?: boolean },
) => {
  const { isAuthenticated } = useAuth();
  const fetchToken = useTokenFetcher();
  const callerEnabled = options?.enabled ?? true;

  return useQuery({
    queryKey: applicationKeys.revisions(projectName, appName),
    queryFn: async () => {
      const token = await fetchToken();
      const response = await listRevisions(projectName, appName, token);
      return response.value ?? [];
    },
    enabled: callerEnabled && (isAuthenticated || isLocalMode()) && !!projectName && !!appName,
    staleTime: 30 * 1000,
    refetchInterval: (query) => (isRevisionPreparing(query.state.data) ? 3_000 : false),
  });
};

/**
 * Fetches available image tags for the application.
 * Tags change infrequently, so use a longer staleTime.
 */
export const useImageTags = (projectName: string, appName: string) => {
  const { isAuthenticated } = useAuth();
  const fetchToken = useTokenFetcher();

  return useQuery({
    queryKey: applicationKeys.imageTags(projectName, appName),
    queryFn: async () => {
      const token = await fetchToken();
      return getImageTags(projectName, appName, token);
    },
    enabled: (isAuthenticated || isLocalMode()) && !!projectName && !!appName,
    staleTime: 5 * 60 * 1000,
  });
};

// =============================================================================
// MUTATIONS
// =============================================================================

/**
 * Starts an application. Invalidates the application status query
 * to trigger fresh polling for the Running transition.
 */
export const useStartApplication = (projectName: string, appName: string) => {
  const fetchToken = useTokenFetcher();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const token = await fetchToken();
      return startApplication(projectName, appName, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: applicationKeys.status(projectName, appName) });
    },
  });
};

/**
 * Stops an application. Invalidates the application status query
 * to trigger fresh polling for the Stopped transition.
 */
export const useStopApplication = (projectName: string, appName: string) => {
  const fetchToken = useTokenFetcher();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const token = await fetchToken();
      return stopApplication(projectName, appName, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: applicationKeys.status(projectName, appName) });
    },
  });
};

/**
 * Updates the scale configuration. Caches the response directly
 * (like env-settings) since a GET immediately after may return stale data.
 */
export const useUpdateScaleConfiguration = (projectName: string, appName: string) => {
  const fetchToken = useTokenFetcher();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: ScaleConfiguration) => {
      const token = await fetchToken();
      return updateScaleConfiguration(projectName, appName, token, config);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(applicationKeys.scale(projectName, appName), data);
    },
  });
};

/**
 * Sets the container image (changes runtime version).
 * Invalidates revisions + application status since the change
 * creates a new revision and may briefly affect runningStatus.
 */
export const useSetImage = (projectName: string, appName: string) => {
  const fetchToken = useTokenFetcher();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: SetImageRequest) => {
      const token = await fetchToken();
      return setImage(projectName, appName, token, request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: applicationKeys.revisions(projectName, appName) });
      queryClient.invalidateQueries({ queryKey: applicationKeys.status(projectName, appName) });
      queryClient.invalidateQueries({ queryKey: applicationKeys.imageTags(projectName, appName) });
    },
  });
};
